/** @format */

import {AccountsKpiName, AccountsCompanyMediaplanType} from 'graphql/generated/types';

import {buildCumulativeSpentChart, getKpiDisplayName, isLowerBetter} from 'helpers/campaignCards';

import {CampaignDailyStatFromQuery, LastCheckType, SensorGroupsType, KpiType} from '../../../types';
import {CampaignCardData} from '..';
import {calculateKpiFact} from './calculateKpiFact';
import {mapLastChecksToCards} from './mapLastChecksToCards';

interface MapCampaignStatsToCardsParams {
  campaignStats: ReadonlyArray<CampaignDailyStatFromQuery>;
  lastChecks: ReadonlyArray<LastCheckType>;
  sensorGroups: SensorGroupsType;
  mediaplanType?: AccountsCompanyMediaplanType | null;
  primaryKpi?: KpiType;
}

const isKpiBetterThanPlan = (
  kpiFact: number,
  kpiPlan: number,
  kpiName: AccountsKpiName,
): boolean => {
  if (isLowerBetter(kpiName)) {
    return kpiFact <= kpiPlan;
  }
  return kpiFact >= kpiPlan;
};

const determineIsGood = (
  kpiName: AccountsKpiName,
  kpiFact: number,
  kpiPlan: number | undefined,
  totalSpent: number,
  totalConversions: number,
  totalRevenue: number,
): boolean => {
  const kpiUpper = kpiName.toUpperCase();

  // CPL corner cases
  if (kpiUpper === 'CPL') {
    if (totalConversions === 0) return false;
    if (totalSpent === 0 && totalConversions > 0) return true;
  }

  // DRR corner cases
  if (kpiUpper === 'DRR') {
    if (totalRevenue === 0) return false;
    if (totalSpent === 0 && totalRevenue > 0) return true;
  }

  // Если есть план, но факт = 0, значит кампания не работает
  if (kpiPlan !== undefined && kpiFact === 0) return false;

  // Стандартная проверка по плану
  return kpiPlan !== undefined ? isKpiBetterThanPlan(kpiFact, kpiPlan, kpiName) : true;
};

export const mapCampaignStatsToCards = ({
  campaignStats,
  lastChecks,
  sensorGroups,
  mediaplanType,
  primaryKpi,
}: MapCampaignStatsToCardsParams): CampaignCardData[] => {
  const campaignSensorsMap = mapLastChecksToCards(lastChecks, sensorGroups);

  return campaignStats
    .filter(stat => {
      if (!mediaplanType) return true;

      return stat.dailyStats.some(
        day => day.mediaplanType?.toLowerCase() === mediaplanType.toLowerCase(),
      );
    })
    .map(stat => {
      const sensors = campaignSensorsMap.get(stat.campaignId) || [];
      const hasErrors = sensors.length > 0;

      const kpiName = primaryKpi?.name || 'CPL';
      const kpiDisplayName = getKpiDisplayName(kpiName);
      const kpiFact = calculateKpiFact(kpiName, stat);

      const kpiPlanValue = primaryKpi?.value ? Number(primaryKpi.value) : undefined;
      const kpiPlan =
        !Number.isNaN(kpiPlanValue) && kpiPlanValue !== undefined ? kpiPlanValue : undefined;
      const kpiDeviation = kpiPlan !== undefined ? kpiFact - kpiPlan : 0;

      const totalSpent = stat.dailyStats.reduce((sum: number, day) => sum + day.spent, 0);
      const totalConversions = stat.dailyStats.reduce(
        (sum: number, day) => sum + day.conversions,
        0,
      );
      const totalRevenue = stat.dailyStats.reduce((sum: number, day) => sum + day.revenue, 0);

      const isGood = determineIsGood(
        kpiName,
        kpiFact,
        kpiPlan,
        totalSpent,
        totalConversions,
        totalRevenue,
      );

      return {
        campaignId: stat.campaignId,
        campaignName: stat.campaignName,
        spent: totalSpent,
        spentChartData: buildCumulativeSpentChart(stat.dailyStats),
        kpiFact,
        kpiPlan,
        kpiDeviation,
        kpiName,
        kpiDisplayName,
        sensors,
        hasErrors,
        isGood,
      };
    });
};
